import React from 'react';
import { FormattedMessage } from 'react-intl';

import Button from 'ui-components/components/button';
import NotificationService, {
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import messages from './messages';

/**
 * @typedef {import('./api').MfaEnrollment} MfaEnrollment
 */

/**
 * @param {MfaEnrollment} enrollment
 * @returns {boolean}
 */
export const getIsEmail = (enrollment) => enrollment.id.includes('email');

/**
 * @param {MfaEnrollment} enrollment
 * @returns {boolean}
 */
export const getIsSms = (enrollment) => enrollment.id.includes('sms');

/**
 * @param {MfaEnrollment} enrollment
 * @returns {boolean}
 */
export const getIsVoice = (enrollment) => enrollment.id.includes('voice');

/**
 * @param {MfaEnrollment} enrollment
 * @returns {boolean}
 */
export const getIsAuthenticator = (enrollment) => enrollment.id.includes('totp');

/**
 * @param {string} phoneNumber
 * @returns {string}
 */
const getLastFourOfPhone = (phoneNumber) => {
  return phoneNumber.slice(-4);
};

/**
 * @param {MfaEnrollment} enrollment
 * @returns {string | JSX.Element}
 */
export const getDisplayForFactor = (enrollment) => {
  if (getIsEmail(enrollment)) {
    return enrollment.name;
  }

  if (getIsSms(enrollment)) {
    return <FormattedMessage {...messages.smsEnrollment} values={{ lastFour: getLastFourOfPhone(enrollment.name) }} />;
  }

  if (getIsVoice(enrollment)) {
    return <FormattedMessage {...messages.voiceEnrollment} values={{ lastFour: getLastFourOfPhone(enrollment.name) }} />;
  }

  if (getIsAuthenticator(enrollment)) {
    return <FormattedMessage {...messages.totpEnrollment} />;
  }

  return enrollment.type;
};

export const ADDITIONAL_VERIFICATION_FLOWS = {
  mfaRequired: 'mfa_required',
  emailVerificationRequired: 'email_verification_required',
};

/**
 * @param {import('axios').AxiosError} err
 * @returns {boolean}
 */
export const checkResponseIsMfaNeeded = (err) => {
  return err?.response?.data?.Code === ADDITIONAL_VERIFICATION_FLOWS.mfaRequired;
};

/**
 * @param {import('axios').AxiosError} err
 * @returns {boolean}
 */
export const checkResponseIsEmailVerificationRequired = (err) => {
  return err?.response?.data?.Code === ADDITIONAL_VERIFICATION_FLOWS.emailVerificationRequired;
};

/**
 * @param {import('axios').AxiosError} err
 */
export const throwErrorIfAdditionalVerificationNeeded = (err) => {
  if (checkResponseIsMfaNeeded(err) || checkResponseIsEmailVerificationRequired(err)) {
    throw err;
  }
};

export const checkIsErrorMessageUserCancelledMfa = (err) => {
  return err?.message === 'MFA authentication cancelled by user';
};

export const showMfaNeededNotification = () => {
  NotificationService.showNotification({
    title: '',
    notificationType: NOTIFICATION_TYPE_ERROR,
    content: <FormattedMessage {...messages.mfaIsNeededNotification} />,
    duration: NOTIFICATION_DURATIONS.MEDIUM,
  });
};

/**
 * @returns {string} - notificationId
 */
export const showVerifyingInProgressNotification = () => {
  return NotificationService.showNotification({
    title: '',
    notificationType: NOTIFICATION_TYPE_INFO,
    content: <FormattedMessage {...messages.mfaVerificationInProgress} />,
  });
};

/**
 * @param {string} notificationId
 */
export const showVerifyingSuccess = (notificationId) => {
  NotificationService.updateNotification(notificationId, {
    title: '',
    notificationType: NOTIFICATION_TYPE_SUCCESS,
    content: <FormattedMessage {...messages.mfaVerificationSuccess} />,
    duration: NOTIFICATION_DURATIONS.SHORT,
  });
};

/**
 * @param {string} notificationId
 * @param {() => void} onRetry
 */
export const showVerifyingFailed = (notificationId, onRetry) => {
  NotificationService.updateNotification(notificationId, {
    title: '',
    notificationType: NOTIFICATION_TYPE_ERROR,
    content: (
      <FormattedMessage
        {...messages.mfaVerificationFailure}
        values={{
          click: (chunks) => {
            return (
              <Button onClick={onRetry} theme='link'>
                {chunks}
              </Button>
            );
          },
        }}
      />
    ),
  });
};
